import React, { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowLeft, Upload, Users, Send, CheckCircle2, ExternalLink } from "lucide-react";
import api from "../utils/qccaApi";

/** Parse a simple CSV (quoted fields + commas). Returns list of voter objects. */
function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) return [];
  const parseRow = (line) => {
    const out = []; let cur = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQ) {
        if (ch === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else { inQ = false; } }
        else cur += ch;
      } else {
        if (ch === ',') { out.push(cur); cur = ""; }
        else if (ch === '"') { inQ = true; }
        else cur += ch;
      }
    }
    out.push(cur);
    return out.map((s) => s.trim());
  };
  const header = parseRow(lines[0]).map((h) => h.toLowerCase());
  const idxUnit = header.findIndex((h) => ["unit", "unit_number", "unit#", "suite", "apt"].includes(h));
  const idxName = header.findIndex((h) => ["name", "owner", "owner_name", "full_name"].includes(h));
  const idxEmail = header.findIndex((h) => ["email", "e-mail", "email_address"].includes(h));
  if (idxUnit < 0 || idxEmail < 0) {
    throw new Error("CSV must include at least a 'unit' column and an 'email' column.");
  }
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const r = parseRow(lines[i]);
    const unit = (r[idxUnit] || "").trim();
    const email = (r[idxEmail] || "").trim();
    if (!unit || !email) continue;
    rows.push({
      unit,
      name: idxName >= 0 ? (r[idxName] || "").trim() : "",
      email,
    });
  }
  return rows;
}

const SAMPLE_CSV = `unit,name,email
101,Jane Smith,jane@example.ca
102,John Doe,john@example.ca
201,Priya Sharma,priya@example.ca`;

export default function QCCAVoteCreate() {
  const nav = useNavigate();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [deadline, setDeadline] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 14); d.setMinutes(0, 0, 0);
    return d.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm
  });
  const [voters, setVoters] = useState([]);
  const [rawCsv, setRawCsv] = useState("");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null); // {vote_id, total_voters}
  const [outbox, setOutbox] = useState([]);
  const fileRef = useRef(null);

  const canSubmit = useMemo(
    () => title.trim() && description.trim() && deadline && voters.length > 0 && !busy,
    [title, description, deadline, voters, busy]
  );

  const handleCsv = async (file) => {
    try {
      const text = await file.text();
      setRawCsv(text);
      const parsed = parseCsv(text);
      if (parsed.length === 0) throw new Error("No valid rows found.");
      setVoters(parsed);
      toast.success(`Parsed ${parsed.length} voter${parsed.length === 1 ? "" : "s"}.`);
    } catch (e) {
      toast.error(e.message || "Could not parse CSV.");
      setVoters([]);
    }
  };

  const useSample = () => {
    setRawCsv(SAMPLE_CSV);
    try { setVoters(parseCsv(SAMPLE_CSV)); toast.success("Sample CSV loaded."); } catch {}
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      // Convert local datetime-input to ISO with timezone
      const deadlineIso = new Date(deadline).toISOString();
      const res = await api.post("/qcca/votes", {
        title: title.trim(),
        description: description.trim(),
        deadline_iso: deadlineIso,
        voters,
      });
      setCreated({ vote_id: res.data.vote_id, total_voters: res.data.total_voters });
      // Fetch the mocked outbox for preview
      const out = await api.get(`/qcca/votes/${res.data.vote_id}/outbox`);
      setOutbox(out.data.emails || []);
      toast.success("Vote created. Ballots generated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create vote.");
    } finally { setBusy(false); }
  };

  const inputCls =
    "w-full bg-black border border-[#1a1a1a] focus:border-[#C5A52A] text-white placeholder-gray-600 font-mono text-sm px-4 py-3 outline-none transition-colors";

  if (created) {
    return (
      <div className="min-h-screen bg-black text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="bg-[#0a0a0a] border border-[#C5A52A]/60 p-10" data-testid="qcca-vote-created-panel">
            <CheckCircle2 className="w-14 h-14 text-[#C5A52A]" />
            <h1 className="mt-6 font-headline text-4xl text-white leading-tight">Vote created.</h1>
            <p className="mt-3 text-gray-400 leading-relaxed">
              <span className="text-white font-medium">{created.total_voters}</span> tokenized ballot{created.total_voters === 1 ? "" : "s"} generated. In the production build this is where Resend would dispatch each personalized email.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 border border-[#C5A52A]/40 bg-[#C5A52A]/5 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5A52A]">
              MOCKED email delivery — preview outbox below
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={`/qcca/boardroom/votes/${created.vote_id}`}
                className="inline-flex items-center gap-2 bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-mono text-xs uppercase tracking-[0.22em] px-5 py-3 transition-colors">
                Open vote dashboard
              </Link>
              <Link to="/qcca/boardroom/votes"
                className="inline-flex items-center gap-2 border border-[#1a1a1a] hover:border-[#C5A52A] text-gray-300 hover:text-white font-mono text-xs uppercase tracking-[0.22em] px-5 py-3 transition-colors">
                Back to votes
              </Link>
            </div>
          </div>

          {outbox.length > 0 && (
            <div className="mt-10">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A52A]">MOCKED outbox preview</div>
              <h2 className="mt-3 font-headline text-2xl text-white">What your voters would receive</h2>
              <p className="mt-2 text-gray-500 text-sm">
                Click any ballot link to cast a test vote as that unit owner — useful for end-to-end verification before going live.
              </p>
              <div className="mt-6 divide-y divide-[#1a1a1a] border-y border-[#1a1a1a]">
                {outbox.map((m) => (
                  <div key={m.id} className="py-4 grid md:grid-cols-12 gap-4 items-start" data-testid={`qcca-outbox-${m.ballot_id}`}>
                    <div className="md:col-span-4">
                      <div className="font-headline text-white">{m.to_name || "Unit owner"}</div>
                      <div className="font-mono text-[11px] text-gray-500">Unit {m.unit} · {m.to_email}</div>
                    </div>
                    <div className="md:col-span-6">
                      <div className="font-mono text-[11px] text-gray-400">{m.subject}</div>
                    </div>
                    <div className="md:col-span-2 text-right">
                      <a href={m.ballot_url} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 border border-[#1a1a1a] hover:border-[#C5A52A] text-[#C5A52A] font-mono text-[10px] uppercase tracking-[0.22em] px-2 py-1.5 transition-colors">
                        Open ballot <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/qcca/boardroom/votes" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 hover:text-[#C5A52A] mb-6">
          <ArrowLeft className="w-3 h-3" /> Votes
        </Link>

        <div className="mb-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A52A]">Secure Vote-by-Email</div>
          <h1 className="mt-3 font-headline text-4xl text-white leading-tight">Draft a <span className="italic text-[#C5A52A]">resolution.</span></h1>
          <p className="mt-3 text-gray-400 max-w-2xl leading-relaxed">
            Each unit owner on your CSV gets a unique, tokenized ballot link. They'll click to vote Yes / No / Abstain, confirm their unit number, and optionally leave a comment for the board.
          </p>
        </div>

        <form onSubmit={submit} className="bg-[#0a0a0a] border border-[#1a1a1a] p-6 sm:p-10 space-y-6" data-testid="qcca-vote-create-form">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 mb-2">Resolution title *</label>
            <input required value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls}
              placeholder="e.g. Waive the annual financial audit for fiscal 2025"
              data-testid="qcca-vote-title" />
          </div>

          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 mb-2">Full resolution text *</label>
            <textarea required rows={5} value={description} onChange={(e) => setDescription(e.target.value)}
              className={`${inputCls} resize-none`}
              placeholder="The board proposes to waive the annual audit for fiscal year 2025 per s. 62(3) of The Condominium Property Act…"
              data-testid="qcca-vote-description" />
          </div>

          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 mb-2">Voting deadline *</label>
            <input required type="datetime-local" value={deadline} onChange={(e) => setDeadline(e.target.value)}
              className={inputCls} data-testid="qcca-vote-deadline" />
            <p className="mt-1 font-mono text-[10px] text-gray-600">Ballots close automatically at this time.</p>
          </div>

          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 mb-2">Unit owner CSV *</label>
            <label htmlFor="qcca-vote-csv"
              className="flex flex-col sm:flex-row items-center gap-4 border border-dashed border-[#1a1a1a] hover:border-[#C5A52A]/60 p-6 cursor-pointer transition-colors">
              <div className="w-12 h-12 flex items-center justify-center border border-[#C5A52A]/40 text-[#C5A52A]"><Upload className="w-5 h-5" /></div>
              <div className="flex-1 text-center sm:text-left">
                <div className="font-headline text-lg text-white">
                  {voters.length > 0 ? `${voters.length} voter${voters.length === 1 ? "" : "s"} loaded` : "Upload CSV"}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-gray-500 mt-1">
                  Required columns: <span className="text-gray-400">unit</span>, <span className="text-gray-400">email</span> · optional: <span className="text-gray-400">name</span>
                </div>
              </div>
              <input ref={fileRef} id="qcca-vote-csv" type="file" accept=".csv,text/csv" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleCsv(f); }}
                data-testid="qcca-vote-csv-input" />
            </label>
            <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
              <button type="button" onClick={useSample}
                className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5A52A] hover:text-white transition-colors"
                data-testid="qcca-vote-sample-btn">
                Load a sample CSV →
              </button>
              {voters.length > 0 && (
                <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500">
                  <Users className="w-3 h-3 text-[#C5A52A]" /> {voters.length} voter{voters.length === 1 ? "" : "s"}
                </span>
              )}
            </div>

            {voters.length > 0 && (
              <div className="mt-4 border border-[#1a1a1a] bg-black max-h-60 overflow-y-auto">
                <table className="w-full text-sm font-mono">
                  <thead className="sticky top-0 bg-[#0a0a0a] border-b border-[#1a1a1a]">
                    <tr className="text-left text-[10px] uppercase tracking-[0.22em] text-gray-500">
                      <th className="py-2 px-3">Unit</th><th className="py-2 px-3">Name</th><th className="py-2 px-3">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {voters.slice(0, 20).map((v, i) => (
                      <tr key={v.email || `voter-${i}`} className="border-b border-[#1a1a1a]">
                        <td className="py-2 px-3 text-[#C5A52A]">{v.unit}</td>
                        <td className="py-2 px-3 text-gray-300">{v.name || "—"}</td>
                        <td className="py-2 px-3 text-gray-400">{v.email}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {voters.length > 20 && (
                  <div className="px-3 py-2 font-mono text-[10px] text-gray-500 bg-[#0a0a0a] border-t border-[#1a1a1a]">
                    + {voters.length - 20} more…
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="border-t border-[#1a1a1a] pt-6 flex flex-wrap items-center gap-4">
            <button type="submit" disabled={!canSubmit}
              className="inline-flex items-center gap-2 bg-[#C5A52A] disabled:bg-[#1a1a1a] disabled:text-gray-500 hover:bg-[#D4B93A] text-black font-mono text-xs uppercase tracking-[0.22em] px-6 py-3 transition-colors"
              data-testid="qcca-vote-create-submit">
              <Send className="w-4 h-4" /> {busy ? "Creating…" : "Create vote & generate ballots"}
            </button>
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500">
              First vote is included.
            </span>
          </div>
        </form>

        {rawCsv && (
          <details className="mt-6 font-mono text-[11px] text-gray-500">
            <summary className="cursor-pointer hover:text-[#C5A52A]">View raw CSV</summary>
            <pre className="mt-2 p-4 bg-[#0a0a0a] border border-[#1a1a1a] overflow-x-auto whitespace-pre-wrap">{rawCsv}</pre>
          </details>
        )}
      </div>
    </div>
  );
}
